/**
 * ScheduleMeetingSheet — 3-step bottom-sheet wizard
 *
 * Same UX as the "Summarize this User" popup: portal-rendered, slides
 * up from the bottom, swipe-down to dismiss, ~80vh tall, escapes
 * any ancestor stacking context.
 *
 * Steps:
 *   ① Date     — calendar picker
 *   ② Time     — duration chips + 15-min slot grid
 *   ③ Details  — title + description + Request Meeting submit
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar as CalendarComp } from './ui/calendar';
import { Loader2, Calendar, Clock, Send, ChevronLeft, ChevronRight, X, Check, FileText } from 'lucide-react';
import { toast } from 'sonner';

const DURATIONS = [15, 30, 45, 60, 90];
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// Resolve target user's effective hours for a given date.
// Returns { enabled, start, end } or null if availability not loaded.
const effectiveHours = (avail, date) => {
  if (!avail || !date) return null;
  const iso = toIso(date);
  if (avail.overrides && avail.overrides[iso]) {
    const o = avail.overrides[iso];
    if (!o.enabled) return { enabled: false, start: null, end: null };
    const dayKey = DAY_KEYS[date.getDay()];
    return {
      enabled: true,
      start: o.start || avail.default_hours[dayKey].start,
      end: o.end || avail.default_hours[dayKey].end,
    };
  }
  const d = avail.default_hours[DAY_KEYS[date.getDay()]];
  return { enabled: !!d.enabled, start: d.start, end: d.end };
};

const buildSlots = (hours, duration) => {
  if (!hours || !hours.enabled) return [];
  const [sh, sm] = hours.start.split(':').map(Number);
  const [eh, em] = hours.end.split(':').map(Number);
  const startMin = sh * 60 + sm;
  // Last slot must finish by `end` — so the latest start is end - duration.
  const endMin = eh * 60 + em - duration;
  const slots = [];
  for (let t = startMin; t <= endMin; t += 15) {
    const h = Math.floor(t / 60);
    const m = t % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  return slots;
};

const slotLabel = (slot) => {
  const [h, m] = slot.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
};

const buildIso = (date, slot) => {
  if (!date || !slot) return null;
  const [h, m] = slot.split(':').map(Number);
  const local = new Date(date);
  local.setHours(h, m, 0, 0);
  return local.toISOString();
};

const friendlyDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString([], {
    weekday: 'short', month: 'short', day: 'numeric',
  });
};

const Stepper = ({ step }) => {
  const stages = [
    { n: 1, label: 'Date', icon: Calendar },
    { n: 2, label: 'Time', icon: Clock },
    { n: 3, label: 'Details', icon: FileText },
  ];
  return (
    <div className="flex items-center justify-between mb-4 pb-3 border-b">
      {stages.map((s, i) => {
        const Icon = s.icon;
        const done = step > s.n;
        const active = step === s.n;
        return (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  done
                    ? 'bg-[#586D52] text-white'
                    : active
                      ? 'bg-[#586D52]/15 text-[#586D52] ring-2 ring-[#586D52]'
                      : 'bg-gray-100 text-gray-400 dark:bg-[#252525]'
                }`}
                data-testid={`stepper-${s.n}`}
              >
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span
                className={`text-xs font-semibold ${
                  active ? 'text-[#586D52]' : done ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < stages.length - 1 && (
              <div className={`h-px flex-1 mx-2 ${done ? 'bg-[#586D52]' : 'bg-gray-200 dark:bg-[#2a2a2a]'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const ScheduleMeetingSheet = ({ api, open, onOpenChange, targetUser }) => {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [avail, setAvail] = useState(null);          // target user's availability doc
  const [availLoading, setAvailLoading] = useState(false);

  // Fetch target user's availability when the sheet opens. Falls
  // back silently to "no restriction" (allow any slot) if the fetch
  // fails so a backend hiccup never blocks booking entirely.
  useEffect(() => {
    if (!open || !targetUser?.id) return;
    let cancelled = false;
    setAvailLoading(true);
    (async () => {
      try {
        const r = await api.get(`/users/${targetUser.id}/availability`);
        if (!cancelled) setAvail(r.data);
      } catch {
        if (!cancelled) setAvail(null);
      } finally {
        if (!cancelled) setAvailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, targetUser?.id, api]);

  // Swipe-down to dismiss (mirrors SummarizePopup behavior).
  const startYRef = useRef(null);
  const [dragY, setDragY] = useState(0);

  // Time slots filtered to the target's availability for the picked day.
  const hours = useMemo(() => effectiveHours(avail, date), [avail, date]);
  const slots = useMemo(() => {
    if (!date) return [];
    if (!avail) {
      // Fallback (availability not loaded / 404): use the legacy
      // 06:00–21:00 window so booking still works.
      return buildSlots({ enabled: true, start: '06:00', end: '21:45' }, duration);
    }
    return buildSlots(hours, duration);
  }, [avail, hours, date, duration]);

  const reset = () => {
    setStep(1);
    setDate(undefined);
    setTime('');
    setDuration(30);
    setTitle('');
    setDescription('');
    setDragY(0);
  };

  const close = () => {
    if (submitting) return;
    reset();
    onOpenChange(false);
  };

  const onTouchStart = (e) => { startYRef.current = e.touches[0].clientY; };
  const onTouchMove = (e) => {
    if (startYRef.current == null) return;
    // Only track drag from the top portion of the sheet (the header)
    // so swiping inside the calendar/scrollable list doesn't dismiss.
    const target = e.target;
    if (target && target.closest('[data-sheet-scroll]')) return;
    const delta = e.touches[0].clientY - startYRef.current;
    if (delta > 0) setDragY(Math.min(delta, 600));
  };
  const onTouchEnd = () => {
    if (dragY > 120) close();
    setDragY(0);
    startYRef.current = null;
  };

  const submit = async () => {
    if (!targetUser?.id || !date || !time) return;
    const iso = buildIso(date, time);
    if (!iso) return;
    if (new Date(iso).getTime() < Date.now() - 60_000) {
      toast.error('Pick a future time');
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        target_user_id: targetUser.id,
        start_time: iso,
        duration_minutes: duration,
      };
      if (title.trim()) body.title = title.trim();
      if (description.trim()) body.description = description.trim();
      await api.post('/meeting-requests', body);
      toast.success(`Meeting request sent to ${targetUser.first_name || 'them'}`);
      reset();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.error(detail || 'Time conflict — pick another slot');
        setStep(2);
      } else {
        toast.error(detail || 'Could not send meeting request');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const targetName = targetUser
    ? `${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim() || targetUser.email || 'them'
    : 'them';

  const sheet = (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-end justify-center"
      onClick={close}
      data-testid="schedule-meeting-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        className="bg-white dark:bg-[#1a1a1a] rounded-t-3xl w-full max-w-xl shadow-2xl flex flex-col"
        style={{
          height: '92vh',
          transform: `translateY(${dragY}px)`,
          transition: dragY === 0 ? 'transform 200ms ease-out' : 'none',
          paddingBottom: 'max(env(safe-area-inset-bottom, 0px), var(--kb-offset, 0px))',
        }}
      >
        {/* drag handle */}
        <div className="pt-2 flex justify-center">
          <span className="w-10 h-1.5 rounded-full bg-gray-300 dark:bg-gray-700" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-[#586D52] font-bold">
              Schedule meeting
            </p>
            <p className="font-bold text-sm truncate">with {targetName}</p>
          </div>
          <button
            onClick={close}
            disabled={submitting}
            className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-[#252525] flex items-center justify-center flex-shrink-0"
            data-testid="schedule-meeting-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — scrollable region */}
        <div
          className="flex-1 overflow-y-auto px-4 pt-4 pb-3"
          data-sheet-scroll
        >
          <Stepper step={step} />

          {step === 1 && (
            <div className="space-y-3" data-testid="step-date">
              <p className="text-sm text-muted-foreground">
                What day works for you?
              </p>
              <div className="flex justify-center">
                <CalendarComp
                  mode="single"
                  selected={date}
                  onSelect={(d) => { setDate(d); setTime(''); }}
                  disabled={(d) => {
                    if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                    // Respect the target's availability — grey out
                    // any day they've marked unavailable (day-off
                    // override, or a disabled default weekday).
                    const eff = effectiveHours(avail, d);
                    if (eff && !eff.enabled) return true;
                    return false;
                  }}
                  className="w-full"
                  classNames={{
                    months: 'flex flex-col w-full',
                    month: 'space-y-4 w-full',
                    caption_label: 'text-base font-semibold',
                    table: 'w-full border-collapse',
                    head_row: 'flex w-full',
                    head_cell: 'text-muted-foreground flex-1 font-medium text-sm pb-2',
                    row: 'flex w-full mt-1',
                    cell: 'flex-1 relative p-0 text-center text-base focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:rounded-md',
                    day: 'h-12 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-accent rounded-md text-base',
                    day_selected: 'bg-[#586D52] text-white hover:bg-[#586D52] hover:text-white focus:bg-[#586D52] focus:text-white',
                    day_today: 'bg-accent text-accent-foreground font-semibold',
                    day_outside: 'day-outside text-muted-foreground opacity-40',
                    day_disabled: 'text-muted-foreground opacity-40 line-through',
                  }}
                  data-testid="meeting-calendar"
                />
              </div>
              {date && (
                <p className="text-xs font-semibold text-[#586D52] text-center">
                  Selected: {friendlyDate(date)}
                </p>
              )}
              {availLoading && (
                <p className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Loading {targetUser?.first_name || 'their'}'s availability…
                </p>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-time">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">
                  Duration
                </p>
                <div className="flex flex-wrap gap-2">
                  {DURATIONS.map((d) => (
                    <button
                      key={d}
                      onClick={() => { setDuration(d); setTime(''); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                        duration === d
                          ? 'bg-[#586D52] text-white border-[#586D52]'
                          : 'bg-white dark:bg-[#1a1a1a] text-foreground border-gray-200 dark:border-[#2a2a2a] hover:border-[#586D52]'
                      }`}
                      data-testid={`duration-${d}`}
                    >
                      {d} min
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center justify-between">
                  <span>Time slot</span>
                  <span className="text-[10px] normal-case text-[#586D52]">
                    {friendlyDate(date)}
                    {hours && hours.enabled && ` · ${slotLabel(hours.start)}–${slotLabel(hours.end)}`}
                  </span>
                </p>
                {slots.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-200 dark:border-[#2a2a2a] p-4 text-center text-xs text-muted-foreground" data-testid="no-slots">
                    {hours && !hours.enabled
                      ? `${targetUser?.first_name || 'They'} isn't available on this day. Pick another date.`
                      : 'No slots fit that duration within their hours. Try a shorter meeting.'}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2" data-testid="slot-grid">
                    {slots.map((s) => (
                      <button
                        key={s}
                        onClick={() => setTime(s)}
                        className={`py-2 rounded-lg text-xs font-medium border transition-colors ${
                          time === s
                            ? 'bg-[#586D52] text-white border-[#586D52]'
                            : 'bg-white dark:bg-[#1a1a1a] text-foreground border-gray-200 dark:border-[#2a2a2a] hover:border-[#586D52]'
                        }`}
                        data-testid={`slot-${s}`}
                      >
                        {slotLabel(s)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-details">
              <div className="rounded-xl bg-[#586D52]/10 border border-[#586D52]/20 p-3 text-sm">
                <div className="flex items-center gap-2 text-[#586D52] font-semibold">
                  <Calendar className="w-4 h-4" />
                  {friendlyDate(date)} · {slotLabel(time)} · {duration} min
                </div>
                <p className="text-xs text-muted-foreground mt-1">with {targetName}</p>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                  Title
                </label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={`Meeting with ${targetUser?.first_name || 'teammate'}`}
                  className="mt-1"
                  style={{ fontSize: '16px' }}
                  data-testid="meeting-title-input"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                  Description
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's this meeting about?"
                  rows={3}
                  className="mt-1"
                  style={{ fontSize: '16px' }}
                  data-testid="meeting-notes-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer — sticky action row, mirrors SummarizePopup */}
        <div className="border-t p-3 flex gap-2 items-center bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-md">
          {step > 1 ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={submitting}
              data-testid={`step${step}-back`}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          ) : (
            <div className="flex-1" />
          )}
          <div className="flex-1" />
          {step === 1 && (
            <Button
              className="bg-[#586D52] hover:bg-[#4a5d45] text-white"
              disabled={!date}
              onClick={() => setStep(2)}
              data-testid="step1-next"
            >
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button
              className="bg-[#586D52] hover:bg-[#4a5d45] text-white"
              disabled={!time}
              onClick={() => setStep(3)}
              data-testid="step2-next"
            >
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button
              className="bg-[#586D52] hover:bg-[#4a5d45] text-white"
              disabled={submitting}
              onClick={submit}
              data-testid="meeting-request-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Request Meeting
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
};

export default ScheduleMeetingSheet;
